import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  createApplication,
  getApplication,
  getMicroLoanEligibility,
  getOnboarding,
  listLoanProducts,
  updateApplication,
} from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatCurrency } from "../utils/formatting"

export function ApplyPage() {
  const queryClient = useQueryClient()
  const onboardingQuery = useQuery({
    queryKey: ["portal-onboarding"],
    queryFn: () => getOnboarding().then((response) => response.data),
  })
  const productsQuery = useQuery({
    queryKey: ["portal-loan-products"],
    queryFn: () => listLoanProducts().then((response) => response.data),
  })
  const applicationQuery = useQuery({
    queryKey: ["portal-application"],
    queryFn: () => getApplication().then((response) => response.data),
  })
  const microLoanEligibilityQuery = useQuery({
    queryKey: ["portal-micro-loan-eligibility"],
    queryFn: () => getMicroLoanEligibility().then((response) => response.data),
  })
  const [productId, setProductId] = useState("")
  const [requestedAmount, setRequestedAmount] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("ECOCASH")
  const [destinationAccount, setDestinationAccount] = useState("")
  const [feedback, setFeedback] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    const application = applicationQuery.data
    if (application && (application.status === "DRAFT" || application.status === "SUBMITTED")) {
      setProductId(application.product.productId)
      setRequestedAmount(String((application.requestedPrincipalAmount / 100).toFixed(2)))
      setPaymentMethod(application.paymentMethod)
      setDestinationAccount(application.destinationAccount)
      return
    }

    if (!productId && productsQuery.data?.[0]) {
      setProductId(productsQuery.data[0].productId)
    }
  }, [applicationQuery.data, productId, productsQuery.data])

  const selectedProduct = useMemo(
    () => productsQuery.data?.find((product) => product.productId === productId) ?? null,
    [productId, productsQuery.data],
  )

  const mutation = useMutation({
    mutationFn: async (action: "SAVE_DRAFT" | "SUBMIT" | "WITHDRAW") => {
      if (action === "WITHDRAW") {
        return updateApplication({ action })
      }

      const requestedPrincipalAmount = Math.round(Number(requestedAmount) * 100)
      if (!Number.isFinite(requestedPrincipalAmount) || requestedPrincipalAmount <= 0) {
        throw new Error("Enter a valid requested amount.")
      }

      const payload = {
        productId,
        requestedPrincipalAmount,
        paymentMethod,
        destinationAccount,
        action,
      } as const

      const application = applicationQuery.data
      if (application && application.status === "DRAFT") {
        return updateApplication(payload)
      }

      return createApplication(payload)
    },
    onSuccess: async () => {
      setFeedback("Application updated.")
      setError("")
      await queryClient.invalidateQueries({ queryKey: ["portal-application"] })
      await queryClient.invalidateQueries({ queryKey: ["portal-onboarding"] })
      await queryClient.invalidateQueries({ queryKey: ["portal-profile"] })
    },
    onError: (mutationError) => {
      setError((mutationError as Error).message)
      setFeedback("")
    },
  })

  if (onboardingQuery.isLoading || productsQuery.isLoading || applicationQuery.isLoading) {
    return (
      <div className="page-stack">
        <Skeleton height={220} />
        <Skeleton height={260} />
      </div>
    )
  }

  const onboarding = onboardingQuery.data
  const application = applicationQuery.data
  const microLoanEligibility = microLoanEligibilityQuery.data
  const standardProductsVisible = Boolean(onboarding?.portalStandardProductsEnabled)
  const standardProducts = productsQuery.data ?? []
  const quickLoanEnabled = microLoanEligibility
    ? !microLoanEligibility.reasons.includes("MICRO_LOAN_DISABLED") &&
      !microLoanEligibility.reasons.includes("MICRO_LOAN_PORTAL_DISABLED")
    : false
  if (!onboarding) return null

  const blocksSubmit =
    !onboarding.isProfileComplete ||
    !onboarding.hasRequiredDocuments ||
    onboarding.kycStatus === "ACTION_REQUIRED" ||
    onboarding.kycStatus === "REJECTED"
  const isOpenApplication = application?.status === "DRAFT" || application?.status === "SUBMITTED"
  const showReadOnlyApplication = Boolean(application && !standardProductsVisible)
  const canWithdrawReadOnlyApplication =
    showReadOnlyApplication &&
    (application?.status === "DRAFT" || application?.status === "SUBMITTED")

  return (
    <div className="page-stack">
      <section className="card">
        <div className="section-header">
          <div>
            <p className="eyebrow">Standard borrower application</p>
            <h3>Apply for a standard loan</h3>
          </div>
        </div>
        {feedback ? <div className="status-banner success">{feedback}</div> : null}
        {error ? <div className="status-banner error">{error}</div> : null}
        {blocksSubmit ? (
          <div className="status-banner error">
            {onboarding.kycStatus === "ACTION_REQUIRED"
              ? onboarding.activeKycRequest?.message ??
                "Respond to the requested KYC updates before submitting your application."
              : "Complete your profile and upload all required KYC documents before submitting."}
            <div className="button-row">
              <Link className="ghost-button" to="/onboarding">
                Finish onboarding
              </Link>
            </div>
          </div>
        ) : null}
        {application?.status === "SUBMITTED" ? (
          <div className="status-banner success">
            Your application is under review.
          </div>
        ) : null}
        {application?.status === "CONVERTED" ? (
          <div className="status-banner success">
            This application was converted into loan {application.convertedLoanId}.
          </div>
        ) : null}
        {application?.status === "REJECTED" ? (
          <div className="status-banner error">
            Application rejected: {application.rejectionReason ?? "Review team decision pending details."}
          </div>
        ) : null}
        {quickLoanEnabled && (standardProductsVisible || application) ? (
          <div className="button-row">
            <Link className="ghost-button" to="/quick-loan">
              Explore quick loan
            </Link>
          </div>
        ) : null}
      </section>

      {!standardProductsVisible && !application ? (
        <section className="card list-card">
          <div>
            <p className="eyebrow">Standard products</p>
            <h3>Standard loan products are unavailable</h3>
          </div>
          <p className="subtle">
            This account does not currently have access to standard loan products in the borrower portal.
            {quickLoanEnabled
              ? " Quick loans remain the default option from your dashboard."
              : " Return to the dashboard to review the borrowing options available to your account."}
          </p>
          <div className="button-row">
            {quickLoanEnabled ? (
              <Link className="primary-button" to="/quick-loan">
                Explore quick loan
              </Link>
            ) : null}
            <Link className="ghost-button" to="/">
              Back to dashboard
            </Link>
          </div>
        </section>
      ) : null}

      {standardProductsVisible ? (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Products</p>
              <h3>Select a loan product</h3>
            </div>
          </div>
          {standardProducts.length === 0 ? (
            <p className="empty-copy">No standard loan products are currently available.</p>
          ) : (
            <div className="card-list">
              {standardProducts.map((product) => (
                <button
                  className={`ticket-preview ${product.productId === productId ? "active" : ""}`}
                  key={product.productId}
                  onClick={() => setProductId(product.productId)}
                  type="button"
                >
                  <strong>{product.name}</strong>
                  <span className="subtle">
                    {formatCurrency(product.currency, product.principalMin)} to{" "}
                    {formatCurrency(product.currency, product.principalMax)}
                  </span>
                  <span className="subtle">
                    {Math.round(product.interestRate * 100)}% interest · {product.termDays} days
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      ) : null}

      {showReadOnlyApplication ? (
        <section className="card list-card">
          <div>
            <p className="eyebrow">Current application</p>
            <h3>Standard application remains visible</h3>
          </div>
          <p className="subtle">
            New standard-product submissions are not available to this account, but your current application
            remains visible here.
          </p>
          <div className="list-row">
            <div>
              <strong>Product</strong>
              <p className="subtle">{application?.product.name}</p>
            </div>
            <div className="align-right">
              <strong>Status</strong>
              <p className="subtle">{application?.status}</p>
            </div>
          </div>
          <div className="list-row">
            <div>
              <strong>Requested amount</strong>
              <p className="subtle">{formatCurrency("USD", application?.requestedPrincipalAmount ?? 0)}</p>
            </div>
            <div className="align-right">
              <strong>Payout method</strong>
              <p className="subtle">{application?.paymentMethod}</p>
            </div>
          </div>
          <div className="list-row">
            <div>
              <strong>Destination</strong>
              <p className="subtle">{application?.destinationAccount}</p>
            </div>
            <div className="align-right">
              <strong>Updated</strong>
              <p className="subtle">{application?.updatedAt ?? "-"}</p>
            </div>
          </div>
          {application?.rejectionReason ? (
            <div className="status-banner error">Application rejected: {application.rejectionReason}</div>
          ) : null}
          {canWithdrawReadOnlyApplication ? (
            <div className="button-row">
              <button
                className="ghost-button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate("WITHDRAW")}
                type="button"
              >
                {mutation.isPending ? "Updating..." : "Withdraw"}
              </button>
              <Link className="ghost-button" to="/">
                Back to dashboard
              </Link>
            </div>
          ) : null}
        </section>
      ) : null}

      {application?.status === "CONVERTED" || !standardProductsVisible ? null : (
        <section className="card">
          <div className="section-header">
            <div>
              <p className="eyebrow">Application details</p>
              <h3>{isOpenApplication ? "Current application" : "Start a new application"}</h3>
            </div>
          </div>
          <form
            className="form-grid"
            onSubmit={(event) => {
              event.preventDefault()
              mutation.mutate("SUBMIT")
            }}
          >
            <label>
              Requested amount
              <input
                inputMode="decimal"
                onChange={(event) => setRequestedAmount(event.target.value)}
                placeholder="0.00"
                value={requestedAmount}
              />
            </label>
            {selectedProduct ? (
              <div className="hint-copy">
                Allowed range:{" "}
                {formatCurrency(selectedProduct.currency, selectedProduct.principalMin)} to{" "}
                {formatCurrency(selectedProduct.currency, selectedProduct.principalMax)}
              </div>
            ) : null}
            <label>
              Payout method
              <select onChange={(event) => setPaymentMethod(event.target.value)} value={paymentMethod}>
                <option value="ECOCASH">EcoCash</option>
                <option value="BANK_TRANSFER">Bank transfer</option>
                <option value="INNBUCKS">Innbucks</option>
              </select>
            </label>
            <label>
              Destination account / wallet
              <input
                onChange={(event) => setDestinationAccount(event.target.value)}
                value={destinationAccount}
              />
            </label>
            <div className="button-row">
              <button
                className="ghost-button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate("SAVE_DRAFT")}
                type="button"
              >
                Save draft
              </button>
              {application?.status === "SUBMITTED" ? (
                <button
                  className="ghost-button"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate("WITHDRAW")}
                  type="button"
                >
                  Withdraw
                </button>
              ) : null}
              <button
                className="primary-button"
                disabled={mutation.isPending || blocksSubmit || standardProducts.length === 0}
                type="submit"
              >
                {mutation.isPending ? "Submitting..." : "Submit application"}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  )
}
